import { useEffect, useMemo, useState } from 'react';
import { IconDownload, IconLoader2, IconX } from '@tabler/icons-react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

/**
 * Modal de bulk-export ZIP avec drag-drop entre 4 catégories.
 *
 * Maxim ADR-040 : « outil lorsqu'on télécharge toutes les pièces afin
 * de permettre de les déplacer de dossier répressif à pièce à
 * conclusions si nécessaires ». 4 catégories : dossier répressif /
 * pièces client / pièces adverses / conclusions.
 *
 * - Au mount : fetch /bulk-export/manifest pour récupérer la liste des
 *   pièces + leur catégorie par défaut (PdfFile→dossier_repressif,
 *   PieceClient→pieces_client, DossierConclusionFile→conclusions).
 * - L'utilisateur peut drag-drop n'importe quelle pièce dans n'importe
 *   quelle catégorie pour CET EXPORT (ne modifie pas la classification
 *   interne).
 * - Au submit : POST /bulk-export → ZIP avec 4 sous-dossiers.
 */

type Category = 'dossier_repressif' | 'pieces_client' | 'pieces_adverses' | 'conclusions';

const CATEGORY_LABELS: Record<Category, string> = {
  dossier_repressif: 'Dossier répressif',
  pieces_client: 'Pièces client',
  pieces_adverses: 'Pièces adverses',
  conclusions: 'Conclusions',
};

const CATEGORY_DESC: Record<Category, string> = {
  dossier_repressif: 'PDFs reçus du greffe / parquet / JI',
  pieces_client: 'Pièces fournies par le client',
  pieces_adverses: 'Pièces déposées par une autre partie',
  conclusions: 'Conclusions déposées ou reçues (.docx/.pdf)',
};

type ManifestItem = {
  id: string; // "pdf:<uuid>" | "pc:<uuid>" | "cf:<uuid>"
  category: Category;
  label: string;
  size: number;
};

type Props = {
  dossierId: string;
  dossierRef: string | null;
  onClose: () => void;
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function BulkExportModal({ dossierId, dossierRef, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ManifestItem[]>([]);
  const [dragOverCat, setDragOverCat] = useState<Category | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ categories: Category[]; items: ManifestItem[] }>(
        `/dossiers/${dossierId}/bulk-export/manifest`
      )
      .then((data) => {
        if (!cancelled) setItems(data.items);
      })
      .catch((e) => {
        if (!cancelled) toast.error(e instanceof ApiError ? e.message : 'Manifest échoué');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dossierId]);

  const grouped = useMemo(() => {
    const out: Record<Category, ManifestItem[]> = {
      dossier_repressif: [],
      pieces_client: [],
      pieces_adverses: [],
      conclusions: [],
    };
    for (const it of items) out[it.category].push(it);
    return out;
  }, [items]);

  const move = (id: string, target: Category) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, category: target } : it)));
  };

  const onSubmit = async () => {
    setExporting(true);
    try {
      const safe = (dossierRef || dossierId).replace(/[^\w-]+/g, '_');
      await api.downloadBlob(`/dossiers/${dossierId}/bulk-export`, `dossier-${safe}.zip`, {
        method: 'POST',
        body: { assignments: items.map(({ id, category }) => ({ id, category })) },
      });
      toast.success('Export terminé');
      onClose();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Export échoué');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[90vh] w-full max-w-6xl flex-col rounded-md bg-paper-elevated shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-rule px-5 py-3">
          <div>
            <h2 className="font-serif text-[18px] font-medium text-text-primary">
              Exporter le dossier en ZIP
            </h2>
            <p className="mt-0.5 text-[12px] text-text-secondary">
              Glisse-dépose les pièces dans la catégorie qui te convient pour cet export. La
              classification interne du dossier n'est pas modifiée.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
            aria-label="Fermer"
          >
            <IconX size={18} stroke={1.6} />
          </button>
        </header>

        {loading ? (
          <div className="flex flex-1 items-center justify-center text-[13px] text-text-secondary">
            Chargement du manifeste…
          </div>
        ) : (
          <div className="grid flex-1 grid-cols-2 gap-3 overflow-hidden p-3 lg:grid-cols-4">
            {(Object.keys(CATEGORY_LABELS) as Category[]).map((cat) => {
              const list = grouped[cat];
              return (
                <div
                  key={cat}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverCat(cat);
                  }}
                  onDragLeave={() => setDragOverCat(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData('text/plain');
                    if (id) move(id, cat);
                    setDragOverCat(null);
                  }}
                  className={`flex min-h-0 flex-col rounded-md border-2 transition ${
                    dragOverCat === cat
                      ? 'border-accent bg-accent-light'
                      : 'border-rule bg-paper-card'
                  }`}
                >
                  <header className="border-b border-rule px-3 py-2">
                    <p className="text-[12px] font-semibold uppercase tracking-[0.5px] text-text-primary">
                      {CATEGORY_LABELS[cat]} · {list.length}
                    </p>
                    <p className="mt-0.5 text-[10.5px] text-text-tertiary">{CATEGORY_DESC[cat]}</p>
                  </header>
                  <ul className="flex-1 overflow-y-auto p-2 space-y-1">
                    {list.length === 0 ? (
                      <li className="rounded border border-dashed border-rule p-3 text-center text-[11px] italic text-text-tertiary">
                        glisse une pièce ici
                      </li>
                    ) : (
                      list.map((it) => (
                        <li
                          key={it.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', it.id);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          className="cursor-move rounded border border-rule bg-paper-elevated p-2 text-[11.5px] text-text-primary hover:border-rule-strong"
                          title={it.label}
                        >
                          <div className="truncate font-medium">{it.label}</div>
                          <div className="mt-0.5 text-[10px] text-text-tertiary">
                            {formatBytes(it.size)} · {it.id.split(':')[0]}
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        <footer className="flex items-center justify-between border-t border-rule px-5 py-3">
          <p className="text-[12px] text-text-secondary">
            {items.length} pièce(s) au total — sera exporté en ZIP avec 4 sous-dossiers + un
            MANIFEST.txt
          </p>
          <div className="flex items-center gap-2">
            <Btn type="button" variant="ghost" onClick={onClose} disabled={exporting}>
              Annuler
            </Btn>
            <Btn
              type="button"
              variant="primary"
              onClick={() => void onSubmit()}
              disabled={exporting || items.length === 0}
            >
              {exporting ? (
                <>
                  <IconLoader2 size={14} stroke={1.6} className="animate-spin" />
                  Export en cours…
                </>
              ) : (
                <>
                  <IconDownload size={14} stroke={1.6} />
                  Exporter ZIP
                </>
              )}
            </Btn>
          </div>
        </footer>
      </div>
    </div>
  );
}
